import React from 'react';
import { 
  StyleSheet, 
  TouchableOpacity, 
  Text, 
  ActivityIndicator, 
  ViewStyle, 
  TextStyle 
} from 'react-native';
import { colors, typography, borderRadius } from '../constants/theme';

interface ButtonProps {
  title: string;
  onPress: () => void;
  type?: 'primary' | 'secondary' | 'outline' | 'text';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

const Button = ({
  title,
  onPress,
  type = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  style,
  textStyle,
}: ButtonProps) => {
  const getButtonStyle = (): ViewStyle => {
    let buttonStyle: ViewStyle = {};
    
    // Base styles
    switch (type) {
      case 'primary':
        buttonStyle = {
          backgroundColor: colors.primary,
          borderColor: colors.transparent,
        };
        break;
      case 'secondary':
        buttonStyle = {
          backgroundColor: colors.secondary,
          borderColor: colors.transparent,
        };
        break;
      case 'outline':
        buttonStyle = {
          backgroundColor: colors.transparent,
          borderColor: colors.primary,
          borderWidth: 1,
        };
        break;
      case 'text':
        buttonStyle = {
          backgroundColor: colors.transparent,
          borderColor: colors.transparent,
        };
        break;
    }

    // Size styles
    switch (size) {
      case 'small':
        buttonStyle = {
          ...buttonStyle,
          paddingVertical: 8,
          paddingHorizontal: 16,
        };
        break;
      case 'medium':
        buttonStyle = {
          ...buttonStyle,
          paddingVertical: 12,
          paddingHorizontal: 24,
        };
        break;
      case 'large':
        buttonStyle = {
          ...buttonStyle,
          paddingVertical: 16,
          paddingHorizontal: 32,
        };
        break;
    }

    if (disabled) {
      buttonStyle.opacity = 0.5;
    }

    return buttonStyle;
  };

  const getTextStyle = (): TextStyle => {
    let textStyleConfig: TextStyle = {
      fontSize: typography.fontSize.md,
      fontWeight: typography.fontWeight.medium,
    };

    switch (type) {
      case 'primary':
      case 'secondary':
        textStyleConfig.color = colors.white;
        break;
      case 'outline':
      case 'text':
        textStyleConfig.color = colors.primary;
        break;
    }

    switch (size) {
      case 'small':
        textStyleConfig.fontSize = typography.fontSize.sm;
        break;
      case 'medium':
        textStyleConfig.fontSize = typography.fontSize.md;
        break;
      case 'large':
        textStyleConfig.fontSize = typography.fontSize.lg;
        break;
    }

    return textStyleConfig;
  };

  return (
    <TouchableOpacity
      style={[styles.button, getButtonStyle(), style]}
      onPress={onPress}
      disabled={disabled || loading}
      activeOpacity={0.7}
    >
      {loading ? (
        <ActivityIndicator 
          color={type === 'primary' || type === 'secondary' ? colors.white : colors.primary} 
          size="small" 
        />
      ) : (
        <Text style={[styles.text, getTextStyle(), textStyle]}>{title}</Text>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: borderRadius.md,
    flexDirection: 'row',
  },
  text: {
    textAlign: 'center',
  },
});

export default Button; 